import {
  IPuzzleData,
  CellState,
  ClueType,
  CellType,
  IClue,
  ICell,
  ICellArrowData,
  ArrowDirection,
  PuzzleType,
  FeedFormat,
} from 'js/crossword/types';
import { IPoint } from 'framework/utils/math-utils';

declare const Hyphenopoly: any;

const tags = ['<TITLE>', '<AUTHOR>', '<COPYRIGHT>', '<SIZE>', '<GRID>', '<CLUES>'];

const specialMap: Record<string, ArrowDirection[]> = {
  '1': [ArrowDirection.UpRight],
  '2': [ArrowDirection.Right],
  '3': [ArrowDirection.DownRight],
  '4': [ArrowDirection.LeftDown],
  '5': [ArrowDirection.Down],
  '6': [ArrowDirection.RightDown],
  '7': [ArrowDirection.UpRight, ArrowDirection.Right],
  '8': [ArrowDirection.UpRight, ArrowDirection.DownRight],
  '9': [ArrowDirection.Right, ArrowDirection.DownRight],
  '§': [ArrowDirection.LeftDown, ArrowDirection.Down],
  '$': [ArrowDirection.LeftDown, ArrowDirection.RightDown],
  '%': [ArrowDirection.RightDown, ArrowDirection.Down],
  '&': [ArrowDirection.Right, ArrowDirection.Down],
};

const acrossDirections = [
  ArrowDirection.UpRight,
  ArrowDirection.Right,
  ArrowDirection.DownRight,
];

const dirStartOffset: Record<ArrowDirection, [number, number]> = {
  [ArrowDirection.UpRight]: [0, -1],
  [ArrowDirection.Right]: [1, 0],
  [ArrowDirection.DownRight]: [0, 1],
  [ArrowDirection.Down]: [0, 1],
  [ArrowDirection.LeftDown]: [-1, 0],
  [ArrowDirection.RightDown]: [1, 0]
};

const dirItterationOffset: Record<ArrowDirection, [number, number]> = {
  [ArrowDirection.UpRight]: [1, 0],
  [ArrowDirection.Right]: [1, 0],
  [ArrowDirection.DownRight]: [1, 0],
  [ArrowDirection.Down]: [0, 1],
  [ArrowDirection.LeftDown]: [0, 1],
  [ArrowDirection.RightDown]: [0, 1]
}

const specialKeys = Object.keys(specialMap);

export default function convertArrowWord(rawData: any): IPuzzleData {
  const rawDataLines = rawData.data.split('\n').filter((line) => line !== '');

  const [[title], [author], [copyright], [size], grid, clueLines] = getSections(rawDataLines, tags);
  const [columns, rows] = size.split('x').map(Number);

  const clues: IClue[] = clueLines
    .map((line) => line.split('#'))
    .flat(Infinity)
    .map((line: string, index) => {
      return {
        id: index.toString(),
        number: 0,
        // tempType
        type: ClueType.Across,
        text: line.trimStart().trimEnd(),
        cells: [],
      };
    });

  const cluesCopy = clues.slice();

  const cells: ICell[] = grid
    .join('')
    .split('')
    .map((cellLetter, index) => {
      const x = index % columns;
      const y = Math.floor(index / columns);

      const directions: ArrowDirection[] | undefined = specialMap[cellLetter];

      let arrowData: ICellArrowData[] | undefined;
      if (directions) {
        arrowData = directions.map((direction) => {
          const clue = cluesCopy.shift()!;
          return { clueId: clue.id, clueText: clue.text, arrowDirection: direction };
        });
      }

      return {
        id: `${x}-${y}`,
        type: directions ? CellType.Clue : CellType.Regular,
        letter: '',
        state: CellState.Normal,
        x,
        y,
        number: 0,
        circle: false,
        arrow: arrowData,
        solution: directions ? '' : cellLetter,
      };
    });

  hyphanate(cells);
  populateClueCells(clues, cells);
  populateArrowImages(cells);

  return {
    type: PuzzleType.Arrow,
    id: 'arrow word',
    metaData: {
      title: '',
      author: '',
      date: '',
      format: FeedFormat.GERMAN_ARROW_WORD,
    },
    columns,
    rows,
    clues,
    cells,
  };
}

function hyphanate(cells: ICell[]): void {
  if (!Hyphenopoly) return;
  Hyphenopoly.hyphenators.de.then((hyphanator) => {
    cells.forEach((cell) => {
    if (cell.arrow) {
      cell.arrow.forEach((arrowData) => {
        arrowData.clueText = hyphanator(arrowData.clueText);
      });
    }
  });
});
}

function getSection(lines: string[], start: string, end?: string): string[] {
  return lines.slice(lines.indexOf(start) + 1, end ? lines.indexOf(end) : Infinity);
}

function getSections(lines: string[], tags: string[]): Array<string[]> {
  return tags.map((tag, index, array) => {
    return getSection(lines, tag, array[index + 1]);
  });
}

function populateArrowImages(cells: ICell[]): void {
  cells
    .filter((cell) => cell.arrow)
    .forEach((cell) => {
      cell.arrow!.forEach((arrowData) => {
        const [offsetX, offsetY] = dirStartOffset[arrowData.arrowDirection];
        const cellToAddImage = getCell(cells, cell.x + offsetX, cell.y + offsetY)!;
        if (!cellToAddImage.arrowImages) cellToAddImage.arrowImages = [];
        cellToAddImage.arrowImages.push(arrowData.arrowDirection);
        arrowData.arrowDirection
      });
    });
}

function populateClueCells(clues: IClue[], cells: ICell[]): void {
  const clueCells = cells.filter((cell) => cell.type === CellType.Clue && cell.arrow);
  clueCells.forEach((cell) => {
    cell.arrow!.forEach(({ clueId, arrowDirection}) => {
      const clue = clues.find((clue) => clue.id === clueId)!;
      clue.type = acrossDirections.includes(arrowDirection) ? ClueType.Across : ClueType.Down;
      clue.cells = getCellsForClue(cell, arrowDirection, cells);
    });
  });
}

function getCellsForClue({x, y}: ICell, direction: ArrowDirection, cells: ICell[]) {
  const [startOffsetX, startOffsetY] = dirStartOffset[direction];
  let currentPosition = { x: x + startOffsetX, y: y + startOffsetY };
  let currentCell = getCell(cells, currentPosition.x, currentPosition.y);
  const result: string[] = [];

  while(currentCell && currentCell.type !== CellType.Clue) {
    result.push(currentCell.id);
    const [offsetX, offsetY] = dirItterationOffset[direction];
    currentPosition.x += offsetX;
    currentPosition.y += offsetY;
    currentCell = getCell(cells, currentPosition.x, currentPosition.y);
  }

  return result;
}

function getCell(cells: ICell[], x: number, y: number): ICell | null {
  return cells.find((cell) => cell.x === x && cell.y === y) || null;
}